import { Space } from '@innovation-os-ng/core/models';
import { createReducer, on } from '@ngrx/store';
import { CRUDMessages, MetaState } from '../interfaces/meta-state.interface';
import { SpaceState } from '../interfaces/space-state.interface';
import { SpaceActionTypes, SpaceActions } from './space.actions';

export const initialSpaceState: SpaceState = {
  spaces: {},
  currentSpaceUri: null,
  currentSpace: null,
  ...(<MetaState<Space, CRUDMessages>>{})
};

const reducer = createReducer<SpaceState>(
  initialSpaceState,
  on(SpaceActions.fetchAvailableSpacesByCurrentTenant, (state): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.fetchAvailableSpacesByCurrentTenant.type);    
    return {
      ...state,
      isLoading: true,
      result: {
        ...state.result,
        collectionLoaded: null
      }
    };
  }),
  on(SpaceActions.fetchAvailableSpacesByTenantSlug, (state): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.fetchAvailableSpacesByTenantSlug.type);
    return {
      ...state,
      isLoading: true,
      result: {
        ...state.result,
        collectionLoaded: null
      }
    };
  }),
  on(SpaceActions.fetchAvailableSpacesByTenantUri, (state): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.fetchAvailableSpacesByTenantUri.type);
    return {
      ...state,
      isLoading: true,
      result: {
        ...state.result,
        collectionLoaded: null
      }
    };
  }),
  on(SpaceActions.fetchAvailableSpacesSuccess, (state, { tenantUri, newSpaces }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.fetchAvailableSpacesSuccess.type);
    return {
      ...state,
      spaces : {
        ...state.spaces,
        [tenantUri]: newSpaces 
      },
      isLoading: false,
      result: {
        ...state.result,
        collectionLoaded: {
          message: 'collectionLoaded',
          data: newSpaces
        }
      }
    };
  }),
  on(SpaceActions.fetchAvailableSpacesError, (state, {error}): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.fetchAvailableSpacesError.type);
    return {
      ...state,
      isLoading: false,
      result: {
        ...state.result,
        collectionLoaded: {
          message: 'collectionLoadError',
          error
        }
      }
    };
  }),
  on(SpaceActions.createSpace, (state): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.createSpace.type);
    return {
      ...state,
      isCreating: true,
      result: {
        ...state.result,
        created: null
      }
    };
  }),
  on(SpaceActions.setCreateSpaceSuccess, (state, { space }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setCreateSpaceSuccess.type);
    return {
      ...state,
      // adding the newly created space to list of spaces
      spaces: {
        ...state.spaces,
        [space.tenantUri]: [
          ...(state.spaces[space.tenantUri] || []),
          space
        ]
      },
      isCreating: false,
      result: {
        ...state.result,
        created: {
          message: 'created',
          data: space
        }
      }
    };
  }),
  on(SpaceActions.setCreateSpaceError, (state, { error }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setCreateSpaceError.type);
    return {
      ...state,
      isCreating: false,
      result: {
        ...state.result,
        created: {
          message: 'createError',
          error
        }
      }
    };
  }),
  on(SpaceActions.setCurrentSpaceUri, (state, { spaceUri }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setCurrentSpaceUri.type);
    return {
      ...state,
      currentSpaceUri: spaceUri,
    };
  }),
  on(SpaceActions.setCurrentSpace, (state, { space }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setCurrentSpace.type);
    return {
      ...state,
      currentSpace: space,
    };
  }),
  on(SpaceActions.deleteSpace, (state): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.deleteSpace.type);
    return {
      ...state,
      isDeleting: true,
      result: {
        ...state.result,
        deleted: null
      }
    };
  }),
  on(SpaceActions.setDeleteSpaceSuccess, (state, {spaceUri}): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setDeleteSpaceSuccess.type);
    
    const _spaces: {[tenantUri: string]: Array<Space>} = {}
    Object.entries(state.spaces).forEach(([tenantUri, spaces]) => {
      _spaces[tenantUri] = spaces.filter(space => space.uri !== spaceUri)
    })
    
    return {
      ...state,
      isDeleting: false,
      spaces: _spaces,
      result: {
        ...state.result,
        deleted: {
          message: 'deleted'
        }
      }
    };
  }),
  on(SpaceActions.setDeleteSpaceError, (state, { error }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setDeleteSpaceError.type);
    return {
      ...state,
      isDeleting: false,
      result: {
        ...state.result,
        deleted: {
          message: 'deleteError',
          error
        }
      }
    };
  }),
  on(SpaceActions.updateSpace, (state): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.updateSpace.type);
    return {
      ...state,
      isUpdating: true,
      result: {
        ...state.result,
        updated: null
      }
    };
  }),
  on(SpaceActions.setUpdateSpaceSuccess, (state, { space }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setUpdateSpaceSuccess.type);
    return {
      ...state,
      isUpdating: false,
      result: {
        updated: {
          ...state.result,
          message: 'updated',
          data: space
        }
      }
    };
  }),
  on(SpaceActions.setUpdateSpaceError, (state, { error }): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.setUpdateSpaceError.type);
    return {
      ...state,
      isUpdating: false,
      result: {
        ...state.result,
        updated: {
          message: 'updateError',
          error
        }
      }
    };
  }),
  on(SpaceActions.reset, (state): SpaceState => {
    console.debug('Action dispatched: ', SpaceActions.reset.type);
    return {
      ...state,
      ...initialSpaceState
    };
  })
);

export const SpaceReducer = (state = initialSpaceState, actions: SpaceActionTypes): SpaceState => {
  return reducer(state, actions);
};
